// Mock Data Service for Frontend-Only Implementation
// This service provides simulated data for all dashboard features without backend dependencies

export interface ToolOutput {
  id: string;
  tool: string;
  status: 'success' | 'error' | 'pending';
  result: {
    summary?: string;
    data?: any;
    image?: string;
    chart?: any;
  };
  timestamp: string;
  duration?: number;
}

export interface OrchestrationEvent {
  id: string;
  type: 'intent' | 'tool' | 'result';
  content: string;
  timestamp: string;
  toolName?: string;
  status?: 'success' | 'error' | 'pending';
}

export interface PatientData {
  id: string;
  name: string;
  age: number;
  gender: string;
  mrn: string;
  lastVisit: string;
  diagnosis: string;
  status: 'stable' | 'improving' | 'declining';
  vitals: {
    bloodPressure: string;
    heartRate: number;
    temperature: number;
    oxygenSaturation: number;
  };
  medications: string[];
  notes: string;
}

export interface SessionHistory {
  id: string;
  timestamp: string;
  request: string;
  response: ToolOutput[];
  duration: number;
}

export interface Metrics {
  successRate: number;
  averageLatency: number;
  totalRequests: number;
  errorRate: number;
  uptime: string;
}

// Mock Tool Outputs
export const mockToolOutputs: ToolOutput[] = [
  {
    id: '1',
    tool: 'summarize_visit',
    status: 'success',
    result: {
      summary: 'Patient A shows mild improvement in symptoms. Blood pressure normalized, heart rate stable.'
    },
    timestamp: '2025-10-10T10:45:05Z',
    duration: 1200
  },
  {
    id: '2',
    tool: 'analyze_lab_results',
    status: 'success',
    result: {
      summary: 'Lab results within normal range. Cholesterol levels improved from last visit.',
      data: {
        cholesterol: 180,
        glucose: 95,
        hemoglobin: 14.2
      }
    },
    timestamp: '2025-10-10T10:46:12Z',
    duration: 800
  },
  {
    id: '3',
    tool: 'generate_report',
    status: 'success',
    result: {
      summary: 'Comprehensive patient report generated successfully.',
      chart: {
        type: 'line',
        data: [120, 118, 115, 112, 110]
      }
    },
    timestamp: '2025-10-10T10:47:30Z',
    duration: 2000
  },
  {
    id: '4',
    tool: 'predict_risk',
    status: 'error',
    result: {
      summary: 'Risk prediction failed due to insufficient data.'
    },
    timestamp: '2025-10-10T10:48:15Z',
    duration: 500
  }
];

// Mock Orchestration Events
export const mockOrchestrationEvents: OrchestrationEvent[] = [
  {
    id: '1',
    type: 'intent',
    content: 'Analyze patient visit data and generate summary',
    timestamp: '2025-10-10T10:44:30Z'
  },
  {
    id: '2',
    type: 'tool',
    content: 'Executing summarize_visit tool',
    timestamp: '2025-10-10T10:44:35Z',
    toolName: 'summarize_visit',
    status: 'success'
  },
  {
    id: '3',
    type: 'result',
    content: 'Visit summary generated successfully',
    timestamp: '2025-10-10T10:45:05Z'
  },
  {
    id: '4',
    type: 'tool',
    content: 'Executing analyze_lab_results tool',
    timestamp: '2025-10-10T10:45:10Z',
    toolName: 'analyze_lab_results',
    status: 'success'
  },
  {
    id: '5',
    type: 'result',
    content: 'Lab analysis completed',
    timestamp: '2025-10-10T10:46:12Z'
  }
];

// Mock Patient Data
export const mockPatientData: PatientData[] = [
  {
    id: '1',
    name: 'John Smith',
    age: 65,
    gender: 'Male',
    mrn: 'MRN-2024-001',
    lastVisit: '2025-10-10',
    diagnosis: 'Hypertension, Type 2 Diabetes',
    status: 'improving',
    vitals: {
      bloodPressure: '130/80',
      heartRate: 72,
      temperature: 98.6,
      oxygenSaturation: 98
    },
    medications: ['Metformin', 'Lisinopril', 'Atorvastatin'],
    notes: 'Patient responding well to current treatment plan. Continue monitoring blood pressure.'
  },
  {
    id: '2',
    name: 'Sarah Johnson',
    age: 42,
    gender: 'Female',
    mrn: 'MRN-2024-002',
    lastVisit: '2025-10-09',
    diagnosis: 'Migraine, Anxiety',
    status: 'stable',
    vitals: {
      bloodPressure: '115/75',
      heartRate: 68,
      temperature: 98.4,
      oxygenSaturation: 99
    },
    medications: ['Sumatriptan', 'Sertraline'],
    notes: 'Migraine frequency reduced. Patient reports improved quality of life.'
  },
  {
    id: '3',
    name: 'Michael Brown',
    age: 58,
    gender: 'Male',
    mrn: 'MRN-2024-003',
    lastVisit: '2025-10-08',
    diagnosis: 'COPD, Heart Disease',
    status: 'declining',
    vitals: {
      bloodPressure: '140/90',
      heartRate: 85,
      temperature: 99.1,
      oxygenSaturation: 92
    },
    medications: ['Albuterol', 'Prednisone', 'Metoprolol'],
    notes: 'Respiratory function declining. Consider adjusting treatment plan.'
  }
];

// Mock Session History
export const mockSessionHistory: SessionHistory[] = [
  {
    id: '1',
    timestamp: '2025-10-10T10:44:30Z',
    request: 'Analyze patient visit data and generate summary',
    response: mockToolOutputs.slice(0, 3),
    duration: 3000
  },
  {
    id: '2',
    timestamp: '2025-10-10T09:30:15Z',
    request: 'Generate lab report for patient MRN-2024-001',
    response: mockToolOutputs.slice(1, 2),
    duration: 1500
  },
  {
    id: '3',
    timestamp: '2025-10-10T08:15:45Z',
    request: 'Predict risk factors for patient MRN-2024-003',
    response: mockToolOutputs.slice(3, 4),
    duration: 500
  }
];

// Mock Metrics
export const mockMetrics: Metrics = {
  successRate: 87.5,
  averageLatency: 1250,
  totalRequests: 1247,
  errorRate: 12.5,
  uptime: '99.8%'
};

// Service Functions
export class MockDataService {
  // Tool Outputs
  static getToolOutputs(): ToolOutput[] {
    return mockToolOutputs;
  }

  static getToolOutputById(id: string): ToolOutput | undefined {
    return mockToolOutputs.find(output => output.id === id);
  }

  // Orchestration Events
  static getOrchestrationEvents(): OrchestrationEvent[] {
    return mockOrchestrationEvents;
  }

  // Patient Data
  static getPatientData(): PatientData[] {
    return mockPatientData;
  }

  static getPatientById(id: string): PatientData | undefined {
    return mockPatientData.find(patient => patient.id === id);
  }

  // Session History
  static getSessionHistory(): SessionHistory[] {
    return mockSessionHistory;
  }

  static addSessionHistory(session: Omit<SessionHistory, 'id'>): SessionHistory {
    const newSession: SessionHistory = {
      ...session,
      id: Date.now().toString()
    };
    mockSessionHistory.unshift(newSession);
    return newSession;
  }

  // Metrics
  static getMetrics(): Metrics {
    return mockMetrics;
  }

  static updateMetrics(): Metrics {
    // Simulate real-time metrics updates
    const updatedMetrics = {
      ...mockMetrics,
      successRate: Math.max(80, Math.min(95, mockMetrics.successRate + (Math.random() - 0.5) * 2)),
      averageLatency: Math.max(800, Math.min(2000, mockMetrics.averageLatency + (Math.random() - 0.5) * 200)),
      totalRequests: mockMetrics.totalRequests + Math.floor(Math.random() * 5)
    };
    Object.assign(mockMetrics, updatedMetrics);
    return updatedMetrics;
  }

  // Simulate API calls with delays
  static async simulateApiCall<T>(data: T, delay: number = 1000): Promise<T> {
    return new Promise((resolve) => {
      setTimeout(() => resolve(data), delay);
    });
  }
}

export default MockDataService;








